import { Box, IconButton, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { GridRenderCellParams, useGridApiContext } from '@mui/x-data-grid-premium';
import React, { MouseEvent, ReactNode, useCallback, useMemo } from 'react';
import { CreativeCredit } from 'sr-types/lib/shortlist/v1/graphql';
import { KEY_CREDITS_WRAP_LIMIT } from '../../..';
import EllipsisText from '../../../../components/EllipsisText';
import I18n from '../../../../i18n/I18n';
import { Icons } from '../../../../icons/Icons';
import { MultiSelectChips } from '../../../../list/MultiSelectChips';
import { KEY_CREDITS_DISPLAY_MODE } from '../../../constants';
import { coerceKeyCreditsList } from '../../../data/utils';
import type { KeyCreditsDisplayMode, KeyCreditsLineItemRow, KeyCreditsWrapLimit } from '../../../types';
import { clampKeyCreditsWrapLimit, formatKeyCredit, formatKeyCreditsDisplayText } from '../../utils';
import KeyCreditsWrapLimitRead from '../internal/KeyCreditsWrapLimitRead';

const textWrapperSx = { width: '100%', height: '100%', display: 'flex', alignItems: 'center' };

export type KeyCreditsCellProps = {
    params: GridRenderCellParams;
    displayMode?: KeyCreditsDisplayMode;
    wrapLimit?: KeyCreditsWrapLimit;
    isAIEnabled?: boolean;
    allowAdd?: boolean;
    onOpenKeyCreditsAI?: (row: KeyCreditsLineItemRow) => void;
};

export default function KeyCreditsCell({
    params,
    displayMode = KEY_CREDITS_DISPLAY_MODE.lineBreak,
    wrapLimit = KEY_CREDITS_WRAP_LIMIT.default,
    onOpenKeyCreditsAI,
    isAIEnabled = false,
    allowAdd = true
}: KeyCreditsCellProps) {
    const apiRef = useGridApiContext();
    const credits = coerceKeyCreditsList(params.value as ReadonlyArray<CreativeCredit | null | undefined> | null);
    const clampedWrapLimit = clampKeyCreditsWrapLimit(wrapLimit);
    const labels = useMemo(() => credits.map(formatKeyCredit).filter(Boolean), [credits]);
    const text = useMemo(() => formatKeyCreditsDisplayText(labels, displayMode), [labels, displayMode]);
    const lineBreakPreviewText = useMemo(
        () => labels.slice(0, clampedWrapLimit).join('\n'),
        [clampedWrapLimit, labels]
    );
    const isChipMode = displayMode === KEY_CREDITS_DISPLAY_MODE.chip;
    const isLineBreakMode = displayMode === KEY_CREDITS_DISPLAY_MODE.lineBreak;
    const editable = Boolean(params.isEditable);
    const exceedsWrapLimit = isLineBreakMode && labels.length > clampedWrapLimit;
    const row = params.row as KeyCreditsLineItemRow;
    const showAiIcon = isAIEnabled && editable && Boolean(onOpenKeyCreditsAI) && labels.length === 0;

    const textSx = useMemo<SxProps<Theme> | undefined>(
        () => (isLineBreakMode ? { whiteSpace: 'pre-line' } : undefined),
        [isLineBreakMode]
    );

    const startEdit = useCallback(
        (e: MouseEvent) => {
            if (!editable) return;
            e.stopPropagation();
            apiRef.current.startCellEditMode({ id: params.id, field: params.field });
        },
        [apiRef, editable, params.field, params.id]
    );

    const handleAiClick = useCallback(
        (e: MouseEvent) => {
            e.stopPropagation();
            onOpenKeyCreditsAI?.(row);
        },
        [onOpenKeyCreditsAI, row]
    );
    const handleCellDoubleClick = useCallback(
        (e: MouseEvent) => {
            if (!editable) return;
            startEdit(e);
        },
        [editable, startEdit]
    );

    const aiIcon = showAiIcon ? (
        <Tooltip title={<I18n token="shortlist.key.credits.ai.tooltip" />}>
            <IconButton size="small" onClick={handleAiClick} sx={{ p: 0, ml: 0.25, flexShrink: 0 }}>
                <Icons.AutoAwesomeGradient sx={{ fontSize: 14 }} />
            </IconButton>
        </Tooltip>
    ) : null;

    const labelWithAiIcon = (label: ReactNode) => (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                maxWidth: '100%',
                gap: 1,
                minWidth: 0
            }}
        >
            {label}
            {aiIcon}
        </Box>
    );

    if (!text) {
        if (!editable || !allowAdd) return null;
        return (
            <Box sx={{ ...textWrapperSx }} onDoubleClick={handleCellDoubleClick}>
                {labelWithAiIcon(
                    <Typography
                        component="span"
                        variant="body2"
                        onClick={startEdit}
                        sx={{ fontStyle: 'italic', color: 'text.disabled', cursor: 'pointer' }}
                    >
                        <I18n token="shortlist.line.item.key.credits.placeholder" />
                    </Typography>
                )}
            </Box>
        );
    }

    if (isChipMode) {
        return (
            <Box sx={textWrapperSx} onDoubleClick={editable ? handleCellDoubleClick : undefined}>
                <MultiSelectChips selectedValues={labels} readOnly allValuesVisible={false} stopOverflowPropagation />
            </Box>
        );
    }

    if (editable && exceedsWrapLimit) {
        return (
            <Box sx={textWrapperSx} onDoubleClick={handleCellDoubleClick}>
                <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                    <KeyCreditsWrapLimitRead
                        text={text}
                        previewText={isLineBreakMode ? lineBreakPreviewText : undefined}
                        wrapLimit={clampedWrapLimit}
                        isLineBreakMode={isLineBreakMode}
                        onStartEdit={startEdit}
                        totalCount={labels.length}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={textWrapperSx} onDoubleClick={editable ? handleCellDoubleClick : undefined}>
            <EllipsisText text={text} clampLines={clampedWrapLimit} sx={textSx} />
        </Box>
    );
}
